/**
 * reduce — the production single-visible VN step→StageState reducer, lifted from
 * the online player (moonshort-backend preview `applyStepToStage` /
 * `advanceUntilHalt`), plus a thin framework-free wrapper around the shared
 * cursor walker (`@lunaverse-ide/player-core`) that exposes exactly what an
 * in-IDE store needs to:
 *   - advance one "beat" until a halt (Line / new-character entrance / pause /
 *     decision / episode end),
 *   - read out the active choice options / cg / phone at a halt,
 *   - descend into a chosen option's sub-steps,
 *   - resume past a cg_show.
 *
 * This file has ZERO React / DOM dependencies — it is the pure model layer.
 * The host store drives it; `PlayerStage.tsx` only renders the resulting
 * `StageState` (+ a few derived flags).
 *
 * ─── Single-visible model ───
 * At most ONE character portrait is on screen (`stage.visible`), plus a
 * `lastPose` memory (character → last sprite url) so a dialogue line for an
 * off-screen character can implicitly re-show its last sprite. This REPLACES
 * the older 3-slot (charLeft/charCenter/charRight) model.
 *
 * The dialogue/bubble BUBBLE side is the protagonist-left / other-right inline
 * rule (`protagonistName && character === protagonistName ? "left" : "right"`),
 * a 2-value projection of the single placement law. `forcedSlotFor` is the
 * retained, test-guarded export of that law (delegates to the shared
 * `placeCharacter`); the single-visible switch computes side inline rather than
 * routing through it.
 *
 * ─── Seams vs the online player (intentional divergences) ───
 * - protagonistName arrives via `ReduceCtx`, not from a resolved session slug.
 * - asset urls are already host-rewritten to webview URIs; treated as
 *   ready-to-use (no resolver).
 * - audio steps (music / music_stop / sfx / music_play / music_crossfade /
 *   music_fadeout / sfx_play) are no-ops here (AudioManager is out of scope).
 *   They still consume their slot in the walk; they just don't touch StageState.
 * - the online vocabulary is keyed to the IDE player-core engine: the lifted
 *   single-visible LOGIC handles the NEWER step types the engine emits
 *   (char_look / char_hide / char_move / phone_hide / music_*), which the
 *   backend preview switch never had.
 * - epoch counters live on `ReduceCtx.epochs` (the online player held them in
 *   component refs `bgEpochRef` / `charEpochRef`). The store owns ONE ctx for
 *   the whole replay so the monotonic bump semantics match exactly.
 * - the halt-detection side signals the online player kept in refs
 *   (`newCharTapRef` / `pendingPauseRef`) are surfaced on `ReduceCtx.flags`
 *   so the wrapper can reproduce `advanceUntilHalt`'s break conditions.
 */
import { EMPTY_STAGE } from "./stage.js";
import { StepPlayer, advanceSibling, popToParentSibling } from "@lunaverse-ide/player-core";
import { pastelFor } from "./primitives.js";
import { placeCharacter } from "../stage/index.js";
export function createReduceCtx(protagonistName) {
    return {
        protagonistName,
        epochs: { bg: 0, char: 0 },
        flags: { newCharEntrance: false, pendingPauseClicks: 0 },
    };
}
/**
 * Hard layout rule (2026-05): only the protagonist may stand on the left.
 * Every other character is forced to the right. When the protagonist is
 * unknown (null) the caller's `fallback` lane is returned. Lives in exactly one
 * place (the shared `placeCharacter`); `forcedSlotFor` is the test-guarded
 * alias.
 */
export function forcedSlotFor(character, fallback, protagonistName) {
    return placeCharacter(character, protagonistName, fallback);
}
// ─────────────────────────────────────────────────────────────
// applyStepToStage — the lifted single-visible reducer.
//
// `next` starts as `{ ...prev, line: null }`. epochs / flags default to
// throwaway sinks (tolerant ctx) so the documented bare-`{ protagonistName }`
// signature works on its own; the advance wrapper always passes the full
// ReduceCtx so epoch / flag state actually persists.
// ─────────────────────────────────────────────────────────────
export function applyStepToStage(stage, step, ctx) {
    const epochs = ctx.epochs ?? { bg: 0, char: 0 };
    const flags = ctx.flags ?? { newCharEntrance: false, pendingPauseClicks: 0 };
    const protagonistName = ctx.protagonistName;
    const prev = stage;
    const next = { ...prev, line: null };
    const s = step;
    const type = s.type;
    // remix-anywhere provenance — surfaced on visible Lines so the renderer can
    // show a sigil. Only dialogue / narrator / you carry it (the only steps that
    // render a Line). Additive: the backend importer just never sets _remixSource.
    const fromRemix = s._remixSource !== undefined;
    switch (type) {
        case "bg": {
            // Single-visible model does NOT clear `visible` on a bg swap — the
            // portrait persists across a scene change (online behavior). Bump bg
            // epoch to re-trigger the pan.
            epochs.bg += 1;
            next.bgUrl = s.url ?? null;
            next.bgMissing = s.assetMissing === true;
            next.bgName = s.assetName;
            next.bgEpoch = epochs.bg;
            next.emote = null;
            break;
        }
        case "char_show": {
            const character = s.character;
            const url = s.url;
            epochs.char += 1;
            next.visible = {
                character,
                url,
                epoch: epochs.char,
                missing: s.assetMissing === true,
                look: s.assetLook,
            };
            next.lastPose = { ...prev.lastPose, [character]: url };
            // Online model flags a new entrance unconditionally on char_show → the
            // advance loop halts for a tap, every time.
            flags.newCharEntrance = true;
            break;
        }
        case "char_look": {
            // Mid-scene expression change. Single-visible: only re-skin the char if
            // it's the one currently on screen; off-stage looks are no-ops (do NOT
            // bring an off-stage char on screen for an expression change). Update
            // lastPose so a later dialogue implicit-shows the new look.
            const character = s.character;
            if (prev.visible?.character !== character)
                break;
            epochs.char += 1;
            next.visible = {
                character,
                url: s.url,
                epoch: epochs.char,
                missing: s.assetMissing === true,
                look: s.assetLook,
            };
            next.lastPose = { ...prev.lastPose, [character]: s.url };
            break;
        }
        case "char_hide": {
            // Drop the portrait only if the named char is the visible one. Keep
            // lastPose so a later dialogue can still implicit-show.
            const character = s.character;
            if (prev.visible?.character === character)
                next.visible = null;
            break;
        }
        case "char_move":
            // Single-visible has no slots to move between → inert.
            break;
        case "dialogue": {
            const character = s.character;
            // Implicit-show: if the speaker isn't the visible char, try lastPose.
            // The re-shown pose carries no missing/look (it's a known-good prior url).
            if (prev.visible?.character !== character) {
                const pose = prev.lastPose[character];
                if (pose) {
                    epochs.char += 1;
                    next.visible = { character, url: pose, epoch: epochs.char };
                }
                else {
                    next.visible = null;
                }
            }
            // Bubble side follows the role-based render rule, NOT any slot:
            // protagonist→"left", anyone else→"right". protagonistName resolves
            // async; null at this moment → "right".
            const side = protagonistName && character === protagonistName ? "left" : "right";
            next.line = {
                kind: "dialogue",
                speaker: character,
                speakerColor: pastelFor(character),
                text: s.text,
                side,
                fromRemix,
            };
            break;
        }
        case "narrator":
            // Online model clears the portrait on narration.
            next.visible = null;
            next.line = { kind: "narrator", text: s.text, fromRemix };
            break;
        case "you":
            // Online model clears the portrait on a "you" beat.
            next.visible = null;
            next.line = { kind: "you", text: s.text, fromRemix };
            break;
        case "bubble": {
            const character = s.character;
            // Same role-based rule as dialogue: protagonist→left, others→right.
            const side = protagonistName && character === protagonistName ? "left" : "right";
            next.emote = {
                char: character,
                side,
                kind: s.bubble_type,
                id: Date.now() + Math.random(),
            };
            break;
        }
        case "pause": {
            // Requires N extra clicks before next auto-advance. `clicks ?? 1` avoids
            // the NaN that `Math.max(1, undefined)` would produce when clicks is
            // absent (an undefined-clicks pause needs exactly 1 click).
            const clicks = s.clicks;
            flags.pendingPauseClicks = Math.max(1, clicks ?? 1);
            // Treat like a line so the loop halts.
            next.line = { kind: "narrator", text: "…" };
            break;
        }
        case "text_message": {
            next.line = {
                kind: "phone",
                contact: s.character,
                messages: [
                    {
                        from: s.direction === "from" ? "them" : "me",
                        text: s.text,
                    },
                ],
            };
            break;
        }
        case "phone_show": {
            const raw = s;
            const msgs = raw.messages ?? [];
            const contact = msgs.find((m) => m.direction === "from")?.character ?? "Unknown";
            next.line = {
                kind: "phone",
                contact,
                messages: msgs.map((m) => ({
                    from: m.direction === "from" ? "them" : "me",
                    text: m.text,
                })),
            };
            break;
        }
        case "phone_hide":
            if (prev.line?.kind === "phone")
                next.line = null;
            break;
        // ─── audio steps: OUT OF SCOPE — no-ops (AudioManager dropped). Both the
        //     online (music / music_stop / sfx) and IDE-engine (music_play /
        //     music_crossfade / music_fadeout / sfx_play) vocabularies. ───
        case "music":
        case "music_stop":
        case "sfx":
        case "music_play":
        case "music_crossfade":
        case "music_fadeout":
        case "sfx_play":
            break;
        default:
            break;
    }
    return next;
}
/**
 * Walk the player forward until the next halt, applying visual / concurrent
 * steps into `startStage`. Mirrors `advanceUntilHalt`:
 *   - end          → halt:end
 *   - decision     → halt:decision (choice / minigame / cg_show), cursor NOT
 *                    advanced past the decision step (engine returns it in
 *                    place; the store decides where to go next)
 *   - a step/group that sets a Line → halt:line
 *   - a char_show entrance → halt:new-char
 *   - otherwise keep walking
 *
 * `ctx.flags.newCharEntrance` is reset at the start of the walk (mirrors
 * `newCharTapRef.current = false`) and consumed when it triggers a halt.
 */
export function advanceUntilHalt(player, startStage, ctx) {
    let cur = startStage;
    ctx.flags.newCharEntrance = false;
    while (true) {
        const f = player.advance();
        if (f.kind === "end") {
            return { stage: cur, halt: { kind: "end" }, cursor: f.cursor };
        }
        if (f.kind === "decision") {
            return {
                stage: cur,
                halt: {
                    kind: "decision",
                    decisionType: f.decisionType,
                    step: f.payload,
                    cursor: f.cursor,
                },
                cursor: f.cursor,
            };
        }
        if (f.kind === "concurrent" && Array.isArray(f.payload)) {
            for (const st of f.payload)
                cur = applyStepToStage(cur, st, ctx);
            if (cur.line) {
                return { stage: cur, halt: { kind: "line" }, cursor: player.getCursor() };
            }
            if (ctx.flags.newCharEntrance) {
                ctx.flags.newCharEntrance = false;
                return { stage: cur, halt: { kind: "new-char" }, cursor: player.getCursor() };
            }
            continue;
        }
        if (f.kind === "step") {
            cur = applyStepToStage(cur, f.payload, ctx);
            if (cur.line) {
                return { stage: cur, halt: { kind: "line" }, cursor: player.getCursor() };
            }
            if (ctx.flags.newCharEntrance) {
                ctx.flags.newCharEntrance = false;
                return { stage: cur, halt: { kind: "new-char" }, cursor: player.getCursor() };
            }
            continue;
        }
    }
}
// ─────────────────────────────────────────────────────────────
// cursor helpers the store needs to descend into / past decisions, mirroring
// app/core/executor.ts (resolveChoice / resolveCgAck). Re-exported from the
// vendored cursor module so the store never has to know the path grammar.
// ─────────────────────────────────────────────────────────────
/**
 * Entry cursor for a chosen choice option's body. Mirrors
 * `resolveChoice` (executor.ts:83): `[...decisionCursor, "options", optId,
 * "steps"]`. Pass this to `StepPlayer.jumpTo(...)`; the walker's escape-key
 * descent resolves the array → its first atomic step on the next `advance()`.
 * (We stop at the "steps" escape key rather than appending the first step id
 * so the store doesn't have to read the option body — the engine descends.)
 */
export function optionEntryCursor(decisionCursor, optionId) {
    return [...decisionCursor, "options", optionId, "steps"];
}
/**
 * Cursor to resume at AFTER a cg_show (or any decision with an empty body)
 * once the player taps "继续". Mirrors `resolveCgAck` (executor.ts): advance to
 * the cg_show step's next sibling, falling back to popping out of the
 * containing block when the cg_show was the last slot. `episodeSteps` is
 * `episode.steps` (or the EpisodeShape — both accepted by cursor.ts).
 * Returns null at episode end (no sibling, nothing to pop to).
 */
export function resumeAfterDecisionCursor(episodeSteps, decisionCursor) {
    const sib = advanceSibling(episodeSteps, decisionCursor);
    if (sib !== null)
        return sib;
    return popToParentSibling(episodeSteps, decisionCursor);
}
// ─────────────────────────────────────────────────────────────
// re-exports the store will want — one import surface.
// ─────────────────────────────────────────────────────────────
export { StepPlayer, EMPTY_STAGE };
